/**
 * Detecta se a aplicação está rodando como PWA (standalone, fullscreen, minimal-ui)
 * ou equivalente iOS (navigator.standalone) / TWA Android.
 */
export const isStandalonePWA = (): boolean => {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    nav.standalone === true ||
    document.referrer.includes("android-app://")
  );
};

/**
 * Abre uma URL do WhatsApp de forma robusta, tentando primeiro o app nativo
 * e depois o WhatsApp Web como fallback.
 * 
 * Estratégia:
 * 1. Tenta abrir via whatsapp:// (app nativo)
 * 2. Fallback para https://api.whatsapp.com/... (WhatsApp Web)
 * 
 * @param url - URL do WhatsApp (https://api.whatsapp.com/...)
 * @param newTab - Janela da aba vazia (se disponível)
 */
const openWhatsAppUrl = (url: string, newTab: Window | null): void => {
  if (!url) {
    console.warn("[URL_OPEN] URL do WhatsApp vazia");
    return;
  }

  // Extrai phone e text da URL do WhatsApp Web
  let phone = "";
  let text = "";
  
  try {
    const urlObj = new URL(url);
    phone = urlObj.searchParams.get("phone") || "";
    text = urlObj.searchParams.get("text") || "";
  } catch (e) {
    // Se não conseguir fazer parse da URL, tenta extrair manualmente
    const phoneMatch = url.match(/[?&]phone=([^&]*)/);
    const textMatch = url.match(/[?&]text=([^&]*)/);
    phone = phoneMatch ? decodeURIComponent(phoneMatch[1]) : "";
    text = textMatch ? decodeURIComponent(textMatch[1]) : "";
  }
  
  // Cria URL do app nativo (whatsapp://)
  const waLinkApp = `whatsapp://send?phone=${phone}${text ? `&text=${encodeURIComponent(text)}` : ""}`;
  const waLinkWeb = url; // URL original do WhatsApp Web

  const isStandalone = isStandalonePWA();

  try {
    if (newTab && !newTab.closed) {
      // Abre na aba criada (funciona em browser e Android PWA)
      newTab.location.href = waLinkApp;
      
      // Fallback para WhatsApp Web após 1.5s se o app não abrir
      setTimeout(() => {
        if (newTab && !newTab.closed) {
          try {
            newTab.location.href = waLinkWeb;
          } catch (e) {
            console.warn("[URL_OPEN] Erro ao redirecionar para WhatsApp Web:", e);
          }
        }
      }, 1500);
    } else {
      // Tenta abrir em nova aba primeiro (funciona em browser e alguns PWAs)
      let opened = false;
      try {
        const openedTab = window.open(waLinkApp, "_blank");
        if (openedTab) {
          opened = true;
          // Fallback para WhatsApp Web após 1.5s
          setTimeout(() => {
            if (openedTab && !openedTab.closed) {
              try {
                openedTab.location.href = waLinkWeb;
              } catch (e) {
                console.warn("[URL_OPEN] Erro ao redirecionar para WhatsApp Web:", e);
              }
            }
          }, 1500);
        }
      } catch (e) {
        console.warn("[URL_OPEN] window.open falhou, tentando fallback:", e);
      }
      
      // Se window.open falhou e estamos em PWA standalone, usa window.location.href
      // (isso vai abrir o app do WhatsApp e o usuário pode voltar ao PWA depois)
      if (!opened && isStandalone) {
        window.location.href = waLinkApp;
        
        // Fallback para WhatsApp Web após 1.5s
        setTimeout(() => {
          try {
            window.location.href = waLinkWeb;
          } catch (e) {
            console.warn("[URL_OPEN] Erro ao redirecionar para WhatsApp Web:", e);
          }
        }, 1500);
      } else if (!opened) {
        // Se não abriu e não é PWA, tenta apenas o web
        openUrlInNewTab(waLinkWeb);
      }
    }
  } catch (error) {
    console.error("[URL_OPEN] Erro ao abrir WhatsApp:", error);
    // Fallback final: tenta abrir normalmente
    openUrlInNewTab(waLinkWeb);
  }
};

/**
 * Abre uma aba vazia imediatamente (no contexto do clique do usuário)
 * e retorna um objeto com métodos para redirecionar ou fechar a aba.
 * 
 * Esta abordagem funciona no Safari e PWAs porque a aba é aberta
 * sincronamente dentro do contexto do clique do usuário.
 * 
 * @returns Objeto com métodos redirect() e close()
 */
export const openEmptyTab = (): {
  redirect: (url: string) => void;
  close: () => void;
  window: Window | null;
} => {
  let newTab: Window | null = null;

  try {
    // Abre uma aba vazia imediatamente (sincronamente no contexto do clique)
    newTab = window.open("", "_blank");
    
    if (!newTab) {
      console.warn("[URL_OPEN] Não foi possível abrir a aba (pode ter sido bloqueada)");
    }
  } catch (error) {
    console.error("[URL_OPEN] Erro ao abrir aba vazia:", error);
  }

  return {
    redirect: (url: string) => {
      if (!url) {
        console.warn("[URL_OPEN] URL vazia fornecida para redirecionamento");
        if (newTab && !newTab.closed) {
          newTab.close();
        }
        return;
      }

      try {
        // Verifica se é uma URL do WhatsApp
        const isWhatsAppUrl = url.includes("api.whatsapp.com") || url.includes("wa.me");
        
        if (isWhatsAppUrl) {
          // Usa estratégia específica para WhatsApp
          openWhatsAppUrl(url, newTab);
        } else if (newTab && !newTab.closed) {
          // Para outras URLs, redireciona a aba que já está aberta
          newTab.location.href = url;
        } else {
          // Se a aba foi fechada, tenta abrir normalmente
          console.warn("[URL_OPEN] Aba foi fechada, tentando abrir normalmente");
          openUrlInNewTab(url);
        }
      } catch (error) {
        console.error("[URL_OPEN] Erro ao redirecionar aba:", error);
        // Fallback: tenta abrir normalmente
        try {
          openUrlInNewTab(url);
        } catch (fallbackError) {
          console.error("[URL_OPEN] Erro no fallback:", fallbackError);
        }
      }
    },
    close: () => {
      try {
        if (newTab && !newTab.closed) {
          newTab.close();
        }
      } catch (error) {
        console.error("[URL_OPEN] Erro ao fechar aba:", error);
      }
    },
    window: newTab,
  };
};

/**
 * Função para abrir URL de forma compatível com Safari e PWA
 * 
 * Esta função cria um elemento <a> temporário e dispara um clique programático,
 * o que funciona melhor do que window.open() em contextos como Safari e PWAs,
 * especialmente quando chamado dentro de callbacks assíncronos.
 * 
 * @param url - A URL a ser aberta
 */
export const openUrlInNewTab = (url: string): void => {
  if (!url) {
    console.warn("[URL_OPEN] URL vazia fornecida");
    return;
  }

  try {
    // Cria um elemento <a> temporário
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    
    // Adiciona ao DOM temporariamente (necessário para alguns navegadores)
    link.style.display = "none";
    document.body.appendChild(link);
    
    // Dispara o clique programaticamente
    link.click();
    
    // Remove o elemento após um pequeno delay
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
    }, 100);
  } catch (error) {
    console.error("[URL_OPEN] Erro ao abrir URL:", error);
    // Fallback: tenta usar window.open mesmo assim
    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (fallbackError) {
      console.error("[URL_OPEN] Erro no fallback:", fallbackError);
    }
  }
};

